import type { PaymentSource, PaymentTotals } from "@lumora/bridge";
import { errorMessage } from "@lumora/bridge";
import { formatUsd } from "@lumora/core";
import { DataTable, LivePanel, useViewModel } from "@lumora/ui";
import { useCallback, useMemo, useState } from "react";
import { usePlatformPaymentsVm } from "./platformViewModels.js";
import { ConfirmGate } from "./ConfirmGate.js";
import { PlatformPageHeader } from "./PlatformPageHeader.js";
import { PAYMENT_COLUMNS, dateBound, platformPaymentRows, syncSummary } from "./paymentsView.js";

export function PaymentKpis({ totals }: { totals: PaymentTotals | null }) {
  const v = (c: number | undefined) => (totals ? formatUsd(c ?? 0) : "—");
  return (
    <div className="platform-payments-kpis">
      <section className="ops-panel platform-payments-kpi">
        <h3>All time</h3>
        <p className="ops-stat" data-kpi="all-time">
          {v(totals?.allTimeCents)}
        </p>
      </section>
      <section className="ops-panel platform-payments-kpi">
        <h3>Last 30 days</h3>
        <p className="ops-stat" data-kpi="last-30">
          {v(totals?.last30DaysCents)}
        </p>
      </section>
      <section className="ops-panel platform-payments-kpi">
        <h3>Stripe vs offline</h3>
        <p className="ops-stat" data-kpi="by-source">
          {totals
            ? `${formatUsd(totals.bySource.stripe)} / ${formatUsd(totals.bySource.offline)}`
            : "—"}
        </p>
        <p className="muted platform-payments-kpi-note">Stripe / Offline</p>
      </section>
    </div>
  );
}

export function PlatformPaymentsPage() {
  const [source, setSource] = useState<"" | PaymentSource>("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const vm = usePlatformPaymentsVm();
  const { payments, totals } = useViewModel(vm);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);
  const [syncErr, setSyncErr] = useState<string | null>(null);

  const load = useCallback(
    () =>
      vm.loadAll({
        source: source || undefined,
        from: dateBound(from, false),
        to: dateBound(to, true),
        limit: 200,
      }),
    [vm, source, from, to],
  );

  const rows = useMemo(() => platformPaymentRows(payments), [payments]);

  const sync = () => {
    setSyncMsg("Syncing Stripe history…");
    setSyncErr(null);
    void vm
      .syncStripe()
      .then((r) => {
        setSyncMsg(syncSummary(r));
        return load();
      })
      .catch((e: unknown) => {
        setSyncMsg(null);
        setSyncErr(errorMessage(e, "Stripe sync failed"));
      });
  };

  return (
    <div className="site-platform">
      <PlatformPageHeader
        title="Payments"
        lead="Money paid to the platform: Stripe invoices and offline cash, check, or wire."
      />
      <PaymentKpis totals={totals} />

      <section className="ops-panel platform-payments-controls">
        <div className="platform-payments-filters">
          <label>
            Source
            <select
              value={source}
              onChange={(e) => setSource(e.target.value as "" | PaymentSource)}
            >
              <option value="">All</option>
              <option value="stripe">Stripe</option>
              <option value="offline">Offline</option>
            </select>
          </label>
          <label>
            From
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label>
            To
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
        </div>
        <ConfirmGate
          idleLabel="Sync Stripe history"
          confirmLabel="Yes, sync Stripe history"
          onConfirm={sync}
        />
        <div role="status" className="platform-payments-status">
          {syncMsg}
          {syncErr ? <span className="ops-error">{syncErr}</span> : null}
        </div>
      </section>

      <LivePanel title="Payments" load={load} intervalMs={30_000}>
        <DataTable columns={PAYMENT_COLUMNS} rows={rows} empty="No payments recorded yet." />
      </LivePanel>
    </div>
  );
}
